import React, {useEffect, useState} from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import {useMarketerTrackingManagement} from '../hooks/useMarketerTrackingManagement';
import {useVisitDetail} from '../hooks/useVisitDetail';
import {MarketerDateSelector} from '../components/MarketerDateSelector';
import {MarketerPathMap} from '../components/MarketerPathMap';
import {SessionSummaryCard} from '../components/SessionSummaryCard';
import {SessionsHistoryTable} from '../components/SessionsHistoryTable';
import {VisitDetailPanel} from '../components/VisitDetailPanel';
import SessionDetailsModal from '../components/SessionDetailsModal';
import type {WorkSession} from '../models/session.model';

const MarketerTrackingPage: React.FC = () => {
  const {hasPermission} = useAuthorization();
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [detailSession, setDetailSession] = useState<WorkSession | null>(null);

  const {
    marketerOptions,
    loadingMarketers,
    selectedMarketerId,
    selectedDate,
    setSelectedMarketer,
    setSelectedDate,
    selectedSession,
    selectedTrack,
    loadingSelected,
    selectedError,
    history,
    loadingHistory
  } = useMarketerTrackingManagement();

  const {visit, loading: loadingVisit, error: visitError} = useVisitDetail(selectedVisitId);

  // Drop the selected visit and any open session-details modal when the
  // user picks a different marketer or date — both reference data that no
  // longer matches the current selection.
  useEffect(() => {
    setSelectedVisitId(null);
    setDetailSession(null);
  }, [selectedMarketerId, selectedDate]);

  if (!hasPermission(PERMISSIONS.READ_LOCATION_TRACKING)) {
    return (
      <SidebarLayout>
        <PermissionDeniedCard message="ليس لديك صلاحية لعرض مسارات المسوّقين" />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <AppTitle>تتبع المسوّقين</AppTitle>
        </div>

        <MarketerDateSelector
          marketers={marketerOptions}
          selectedMarketerId={selectedMarketerId}
          selectedDate={selectedDate}
          onMarketerChange={setSelectedMarketer}
          onDateChange={setSelectedDate}
          loadingMarketers={loadingMarketers}
        />

        {selectedError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {selectedError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:grid-cols-4">
          <div className="lg:col-span-2 xl:col-span-3">
            <MarketerPathMap
              track={selectedTrack}
              loading={loadingSelected}
              height="max(560px, calc(100vh - 300px))"
              selectedVisitId={selectedVisitId}
              onVisitClick={setSelectedVisitId}
            />
          </div>
          <div className="space-y-4">
            {selectedVisitId !== null ? (
              <VisitDetailPanel
                visit={visit}
                loading={loadingVisit}
                error={visitError}
                onClose={() => setSelectedVisitId(null)}
              />
            ) : (
              <SessionSummaryCard
                session={selectedSession}
                loading={loadingSelected}
              />
            )}
          </div>
        </div>

        {selectedMarketerId !== null && (
          <SessionsHistoryTable
            sessions={history}
            loading={loadingHistory}
            selectedDate={selectedDate}
            onRowClick={(s) => setSelectedDate(s.workDate)}
            onOpenDetails={setDetailSession}
          />
        )}

        <SessionDetailsModal
          isOpen={detailSession !== null}
          sessionId={detailSession?.id ?? null}
          fallbackSession={detailSession}
          onClose={() => setDetailSession(null)}
        />
      </div>
    </SidebarLayout>
  );
};

export default MarketerTrackingPage;
